import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import BaseController from "./BaseController";
import Input, { Input$ValueHelpRequestEvent } from "sap/m/Input";
import JSONModel from "sap/ui/model/json/JSONModel";
import SelectDialog, { SelectDialog$ConfirmEvent, SelectDialog$SearchEvent } from "sap/m/SelectDialog";
import { EnvironmentService } from "../service/Environment.service";
import { ErrorHandler } from "../service/ErrorHandler.service";
import MessageBox from "sap/m/MessageBox";
import Context from "sap/ui/model/Context";
import { Button$PressEvent } from "sap/m/Button";
import DatePicker, { DatePicker$ChangeEvent } from "sap/m/DatePicker";
import { CreateAufgabeDto } from "../model/models";
import TextAreaWithSpeechRecognition from "../speech/TextAreaWithSpeechRecognition";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class TaskDetails extends BaseController {
  private _metaModel: JSONModel;
  private _oModel: JSONModel;
  private _userSearchModel: JSONModel;

  public async getTitle(): Promise<string> {
    return "Aufgabe";
  }

  onInit(): void {
    this._metaModel = new JSONModel({
      minDate: this.getToday(),
    });
    this._oModel = new JSONModel();
    this._userSearchModel = new JSONModel();
    this.setModel(this._oModel);
    this.setModel(this._userSearchModel, "users");
    this.setModel(this._metaModel, "meta");
    this.getRouter().getRoute("taskDetails").attachPatternMatched(this._onPatternMatch, this);
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    this._oModel.setData({});
    const params = this.getUrlSearchParameters();

    const betreff = params.get("betreff");
    const beschreibung = params.get("beschreibung");
    const verantwortlich = params.get("verantwortlich");
    const faelligkeit = params.get("faelligkeit");
    if (betreff) {
      this._oModel.setProperty("/betreff", decodeURIComponent(betreff));
    } else {
      this._oModel.setProperty("/betreff", "Neue Aufgabe");
    }
    if (beschreibung) this._oModel.setProperty("/beschreibung", decodeURIComponent(beschreibung));
    if (verantwortlich) {
      try {
        this._oModel.setProperty("/loadingUser", true);
        const oUser = await $.get(`${EnvironmentService.getBaseUrl()}/user/username?username=${decodeURIComponent(verantwortlich)}`);
        this._oModel.setProperty("/verantwortlich", oUser.username);
        this._oModel.setProperty("/selectedUser", oUser);
      } catch (ex: any) {
        MessageBox.alert(`Benutzer ${decodeURIComponent(verantwortlich)} nicht gefunden.`);
        this._oModel.setProperty("/verantwortlich", undefined);
        this._oModel.setProperty("/selectedUser", undefined);
      } finally {
        this._oModel.setProperty("/loadingUser", false);
        this.validateVeranwortlicher();
      }
    }
    if (faelligkeit) this._oModel.setProperty("/wiedervorlagedatum", decodeURIComponent(faelligkeit));

    history.replaceState(null, null, "#/task");
  };

  private getToday(): Date {
    const date = new Date(new Date());
    date.setHours(0, 0, 0, 0);
    return date;
  }

  onWiedervorlagedatumChange = (oEvent: DatePicker$ChangeEvent) => {
    this.validateWiedervorlagedatum();
  };

  /******************
   * Save and Validation
   ******************/
  handleSavePress = async (oEvent: Button$PressEvent) => {
    const bValdidateBeschreibung = this.validateBeschreibung();
    const bVerantwortlicher = this.validateVeranwortlicher();
    const bWiedervorlagedatum = this.validateWiedervorlagedatum();
    if (bValdidateBeschreibung && bVerantwortlicher && bWiedervorlagedatum) {
      try {
        this._metaModel.setProperty("/isSaving", true);
        await $.post(
          `${EnvironmentService.getBaseUrl()}/aufgaben`,
          JSON.stringify(<CreateAufgabeDto>{
            betreff: this._oModel.getProperty("/betreff"),
            beschreibung: this._oModel.getProperty("/beschreibung"),
            faelligkeitsdatum: this._oModel.getProperty("/wiedervorlagedatum"),
            verantwortlich: this._oModel.getProperty("/selectedUser/username"),
          })
        );
        this._oModel.setData({});
        MessageBox.success("Erfolgreich angelegt");
      } catch (ex: any) {
        ErrorHandler.handleError(ex, { ignorable: true });
      } finally {
        this._metaModel.setProperty("/isSaving", false);
      }
    }
  };

  private validateBeschreibung(): boolean {
    let bValid = true;
    const input = this.byId("beschreibungTextArea") as TextAreaWithSpeechRecognition;
    const sBeschreibung = this._oModel.getProperty("/beschreibung");
    if (!sBeschreibung) {
      input.setValueState("Error");
      input.setValueStateText("Bitte eine Beschreibung eingeben");
      bValid = false;
    } else {
      input.setValueState("None");
      input.setValueStateText("");
      bValid = true;
    }

    return bValid;
  }

  private validateVeranwortlicher(): boolean {
    let bValid = true;
    const input = this.byId("verantwortlicherInput") as Input;
    const oUser = this._oModel.getProperty("/selectedUser");
    if (!oUser) {
      input.setValueState("Error");
      input.setValueStateText("Es muss ein Verantwortlicher gewählt sein");
      bValid = false;
    } else {
      input.setValueState("None");
      input.setValueStateText("");
      bValid = true;
    }

    return bValid;
  }

  private validateWiedervorlagedatum(): boolean {
    let bValid = true;
    const dateField = this.byId("wiedervorlagedatumDatePicker") as DatePicker;
    const sDate = this._oModel.getProperty("/wiedervorlagedatum");
    if (sDate) {
      const dDate = new Date(Date.parse(sDate));
      dDate.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (dDate < now) {
        if (dateField) {
          dateField.setValueState("Error");
          dateField.setValueStateText("Datum muss in der Zukunft liegen");
        }
        bValid = false;
      } else {
        dateField.setValueState("None");
        dateField.setValueStateText("");
        bValid = true;
      }
    } else {
      if (dateField) {
        dateField.setValueState("Error");
        dateField.setValueStateText("Bitte Wiedervorlagedatum angeben");
      }
      bValid = false;
    }

    return bValid;
  }

  /*******************
   * User Select Dialog
   *******************/
  onVerantwortlichHelpRequest = async (oEvent: Input$ValueHelpRequestEvent) => {
    const oUser: any = this._oModel.getProperty("/selectedUser");
    const dialog = (await this.getFragment("UserSelectDialog", { addDependant: true })) as SelectDialog;
    const searchText = oUser ? `${oUser.vorname} ${oUser.name}` : "";
    dialog.open(searchText);
    dialog.fireSearch({ value: searchText });
  };

  onUserConfirm = (oEvent: SelectDialog$ConfirmEvent) => {
    const aContexts = oEvent.getParameter("selectedContexts") as Context[];

    if (aContexts) {
      const oUser = aContexts[0].getObject() as any;
      this._oModel.setProperty("/verantwortlich", oUser.username);
      this._oModel.setProperty("/selectedUser", oUser);
    }

    this.validateVeranwortlicher();
  };

  onUserSearch = async (oEvent: SelectDialog$SearchEvent) => {
    const q = oEvent.getParameter("value");
    if (q) {
      this._userSearchModel.setProperty("/hits", []);
      try {
        this._oModel.setProperty("/userSearchBusy", true);
        await this._userSearchModel.loadData(`${EnvironmentService.getBaseUrl()}/user/search/${q}`);
      } catch (ex: any) {
        ErrorHandler.handleError(ex, { ignorable: true });
      } finally {
        this._oModel.setProperty("/userSearchBusy", false);
      }
    }
  };
}
